import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Search, Sparkles, Users } from "lucide-react";

import { ApiError, api } from "../api/client";
import {
  Badge,
  Card,
  EmptyState,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDateTime, formatMoney, initials } from "../lib/format";
import type { Customer } from "../api/types";

/**
 * The book of clients.
 *
 * **A lens, not a fourth copy of a person.** Every row is a lead that reached
 * `converted`, carrying the contact it became -- the same record Contacts shows.
 * Nothing here is new data, which is why there is no separate customers table
 * to drift out of step with contacts.
 *
 * It sits under Scheduling rather than Customers because that is where a salon
 * looks for "who is coming in", next to the calendar and the bookings that will
 * hang off it.
 */
export function CustomersPage() {
  const [search, setSearch] = useState("");
  const timeZone = useAccountTimeZone();

  const query = useQuery({
    queryKey: ["customers"],
    queryFn: ({ signal }) => api.customers(signal),
  });

  const all = query.data ?? [];
  const rows = all.filter((row) => {
    if (!search.trim()) return true;
    const needle = search.toLowerCase();
    return [row.display_name, row.contact_phone, row.contact_email, row.product_name]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle={
          query.data
            ? `${all.length} ${all.length === 1 ? "person" : "people"} who became customers`
            : "People who stopped being a lead and became a customer"
        }
      />

      <div className="mb-4 relative max-w-md">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, phone or email"
          aria-label="Search customers"
          className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      </div>

      {query.isPending ? (
        <Card padded={false}>
          <TableSkeleton rows={6} />
        </Card>
      ) : query.isError ? (
        <Card>
          <EmptyState
            title="Could not load customers"
            body={
              query.error instanceof ApiError ? query.error.message : "Please try again."
            }
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={28} />}
            title={
              all.length === 0
                ? "Nobody has become a customer yet"
                : "Nobody matches that"
            }
            body={
              all.length === 0
                ? "Mark a lead as Won on the Leads screen and they will appear here, with the contact that was created for them."
                : "Try a different name or number."
            }
            action={
              all.length === 0 ? (
                <Link
                  to="/customers/leads"
                  className="text-sm font-medium text-slate-900 underline"
                >
                  Go to Leads
                </Link>
              ) : null
            }
          />
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Reach them</th>
                <th className="px-4 py-2.5">Last enquiry</th>
                <th className="px-4 py-2.5">Quoted</th>
                <th className="px-4 py-2.5 text-right">Became a customer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <CustomerRow key={row.id} row={row} timeZone={timeZone} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function CustomerRow({
  row,
  timeZone,
}: {
  row: Customer;
  timeZone: string | undefined;
}) {
  // Whichever link conversion recorded. A lead converted against an
  // already-attached contact has no `converted_contact` of its own.
  const contactId = row.converted_contact ?? row.contact;
  const phone = row.phone || row.contact_phone;
  const email = row.email || row.contact_email;

  return (
    <tr className="align-top hover:bg-slate-50">
      <td className="px-4 py-3">
        <div className="flex items-start gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            {initials(row.display_name || "?")}
          </span>
          <div className="min-w-0">
            {contactId ? (
              <Link
                to={`/customers/contacts/${contactId}`}
                className="text-sm font-medium text-slate-900 hover:underline"
              >
                {row.display_name || "Unnamed customer"}
              </Link>
            ) : (
              <p className="text-sm font-medium text-slate-900">
                {row.display_name || "Unnamed customer"}
              </p>
            )}
            <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
              <Sparkles size={11} className="shrink-0 text-emerald-500" />
              Converted from a lead
            </p>
          </div>
        </div>
      </td>

      <td className="px-4 py-3">
        <div className="space-y-0.5 text-xs">
          {phone ? (
            <a
              href={`tel://${phone.replace(/[^\d+]/g, "")}`}
              className="flex items-center gap-1 text-slate-700 hover:underline"
            >
              <Phone size={11} className="shrink-0 text-slate-400" />
              {phone}
            </a>
          ) : null}
          {email ? (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-1 text-slate-700 hover:underline"
            >
              <Mail size={11} className="shrink-0 text-slate-400" />
              {email}
            </a>
          ) : null}
          {!phone && !email ? <span className="text-slate-300">—</span> : null}
        </div>
      </td>

      <td className="px-4 py-3">
        <p className="text-sm text-slate-800">{row.product_name || "—"}</p>
        {row.requested_start_at ? (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
            <MapPin size={11} className="shrink-0" />
            {formatDateTime(row.requested_start_at, timeZone)}
          </p>
        ) : null}
      </td>

      <td className="px-4 py-3 text-sm tabular-nums text-slate-700">
        {row.quoted_price ? formatMoney(row.quoted_price) : "—"}
      </td>

      <td className="px-4 py-3 text-right">
        <p className="text-sm tabular-nums text-slate-600">
          {formatDateTime(row.converted_at, timeZone)}
        </p>
        <Badge tone="emerald">Customer</Badge>
      </td>
    </tr>
  );
}

